import React, { useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PhoneOff, Loader2, Volume2, Smartphone, Globe2, Lock, Mic, MicOff, Grid3x3, Plus, X, Delete } from "lucide-react";

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const PHASE_TEXT = {
  "calling-you": "Calling your phone — answer to continue…",
  announcing: "You answered — preparing your call…",
  "ringing-destination": "Ringing the other side…",
  connected: "Connected",
  ended: "Wrapping up…",
};

const KEYPAD = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

const Dtmf = ({ isBrowser, onSendDigit, onClose }) => {
  const [entered, setEntered] = useState("");
  const press = (d) => {
    setEntered((e) => (e + d).slice(-24));
    if (isBrowser && onSendDigit) onSendDigit(d);
  };
  return (
    <div className="px-6 pt-4 pb-2" data-testid="in-call-dtmf-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold uppercase tracking-widest text-red-200">Keypad</span>
        <button onClick={onClose} className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center active:scale-90 transition-transform" data-testid="dtmf-close-button">
          <X className="h-4 w-4" />
        </button>
      </div>
      {!isBrowser && (
        <p className="text-xs text-red-100/90 bg-white/10 rounded-xl px-3 py-2 mb-3 leading-snug" data-testid="dtmf-callback-hint">
          Tones are sent from the phone we rang — use that phone's own keypad to navigate menus.
        </p>
      )}
      <div className="min-h-[28px] mb-3 text-center font-mono-dial text-xl font-bold tracking-widest break-all" data-testid="dtmf-entered">
        {entered || <span className="text-red-200/50 text-sm font-sans tracking-normal">Enter digits</span>}
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {KEYPAD.map(([d, sub]) => (
          <button key={d} onClick={() => press(d)} data-testid={`dtmf-key-${d}`}
            className="h-14 rounded-2xl bg-white/15 hover:bg-white/25 active:scale-95 transition-all flex flex-col items-center justify-center">
            <span className="font-mono-dial text-2xl font-bold leading-none">{d}</span>
            {sub && <span className="text-[9px] tracking-widest text-red-200 mt-0.5">{sub}</span>}
          </button>
        ))}
      </div>
      {entered && (
        <button onClick={() => setEntered((e) => e.slice(0, -1))} data-testid="dtmf-backspace"
          className="mx-auto mt-3 flex items-center gap-1.5 text-red-100 text-sm font-semibold active:scale-95 transition-transform">
          <Delete className="h-4 w-4" /> Delete
        </button>
      )}
    </div>
  );
};

const Connector = ({ connected }) => (
  <div className="relative h-8 w-0.5 mx-auto my-1 rounded-full overflow-visible" style={{ background: connected ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.25)" }}>
    {connected && (
      <span className="lock-pop absolute -left-[11px] top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-emerald-500 flex items-center justify-center" data-testid="connection-lock">
        <Lock className="h-3 w-3 text-white" />
      </span>
    )}
  </div>
);

export const ActiveCallScreen = ({ call, phase = "calling-you", contactName, elapsed, ending, runningCost, onEnd, muted = false, onToggleMute, onSendDigit, loud = false, onToggleLoud }) => {
  const isTwilio = call.mode === "twilio";
  const isBrowser = call.mode === "browser";
  const isLive = isTwilio || isBrowser;
  const connected = phase === "connected" || call.mode === "simulated";
  const [showPlans, setShowPlans] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [busy, setBusy] = useState("");

  const youLabel = isBrowser ? "This device" : "Your phone";
  const youValue = isBrowser ? "Mic & speakers" : (call.user_phone || "—");

  const reload = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/calls/${call.call_id}/reload`, { action });
      if (data.ok) toast.success(data.message);
      else toast.info(data.message);
      setShowPlans(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const openTopUp = () => {
    window.open("/wallet", "_blank", "noopener");
    toast.info("Top-up opened in a new tab — your call stays connected here.");
  };

  return createPortal(
    <div className="fixed inset-x-0 top-0 z-[60] flex flex-col bg-gradient-to-b from-red-600 to-red-800 text-white call-overlay-h" data-testid="active-call-screen">
      <div className="mx-auto w-full max-w-md flex-1 flex flex-col min-h-0">

        {/* Scrollable status area */}
        <div className="flex-1 overflow-y-auto px-6 pt-6 flex flex-col items-center text-center">
          {isTwilio && call.callback_from && (
            <div data-testid="callback-from-badge">
              <div className="text-[10px] font-bold uppercase tracking-widest text-red-200">ZEEPTEL is calling you from</div>
              <div className="font-mono-dial text-lg font-bold" data-testid="callback-from-number">{call.callback_from}</div>
            </div>
          )}
          {isBrowser && (
            <div className="text-[10px] font-bold uppercase tracking-widest text-red-200 flex items-center justify-center gap-1.5" data-testid="browser-call-badge">
              <Mic className="h-3 w-3" /> Calling from this device — speak into your mic
            </div>
          )}

          {/* Destination */}
          <div className="mt-5">
            <div className="text-3xl leading-none">{call.flag}</div>
            {contactName && <div className="font-bold text-lg mt-1.5" data-testid="active-contact-name">{contactName}</div>}
            <div className="font-mono-dial text-xl font-bold mt-1" data-testid="active-call-number">{call.number}</div>
            <div className="text-xs text-red-200 font-semibold uppercase tracking-wide mt-0.5">{call.country}</div>
          </div>

          {/* Timer */}
          <div className="my-5">
            <div className="inline-flex h-28 w-28 items-center justify-center rounded-full bg-white/15 call-pulse mx-auto">
              <div className="font-mono-dial text-2xl font-bold" data-testid="call-timer">{fmtTime(elapsed)}</div>
            </div>
          </div>

          {/* Phase / connection state */}
          <div className={`text-sm font-bold ${connected ? "text-emerald-200" : "text-white"}`} data-testid="connection-phase">
            {connected && call.mode !== "simulated" ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="red-blink h-2.5 w-2.5 rounded-full bg-emerald-300" data-testid="live-blinker" />
                <Lock className="h-4 w-4" /> Connected — line secured
              </span>
            ) : call.mode === "simulated" ? (
              <span className="inline-flex items-center gap-1.5"><Lock className="h-4 w-4 text-emerald-300" /> Connected (simulated)</span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> {PHASE_TEXT[phase] || PHASE_TEXT["calling-you"]}
              </span>
            )}
          </div>

          {/* Rate + status chips */}
          <div className="grid grid-cols-2 gap-3 mt-5 w-full text-left">
            <div className="bg-white/10 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wide text-red-200 font-bold">Rate</div>
              <div className="font-mono font-bold" data-testid="call-rate">${call.rate.toFixed(3)}/min</div>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wide text-red-200 font-bold">{isLive ? "Status" : "Running cost"}</div>
              <div className="font-mono font-bold" data-testid="call-running-cost">
                {isLive ? (connected ? "In call" : "Connecting…") : `$${runningCost.toFixed(2)}`}
              </div>
            </div>
          </div>

          {/* Low airtime warning */}
          {isLive && connected && call.max_seconds > 0 && (call.max_seconds - elapsed) > 0 && (call.max_seconds - elapsed) <= 120 && (
            <div className="bg-amber-400 text-amber-950 rounded-2xl px-4 py-3 mt-4 text-left flex items-center gap-2.5 w-full" data-testid="low-airtime-warning">
              <span className="text-lg">⚠️</span>
              <div className="text-sm font-bold leading-snug">
                Airtime running low — about {fmtTime(Math.max(0, call.max_seconds - elapsed))} left.
                <span className="font-medium"> Add funds to keep talking.</span>
              </div>
            </div>
          )}

          {/* Twilio in-call IVR reload menu */}
          {isTwilio && (
            <div className="bg-white/10 rounded-2xl p-4 mt-4 text-left w-full" data-testid="in-call-menu">
              <div className="text-[10px] uppercase tracking-widest text-red-200 font-bold mb-2">Voice menu — respond here or on your keypad</div>
              {!showPlans ? (
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => reload("reload_last")} disabled={!!busy}
                    className="flex items-center gap-2 bg-white text-red-700 rounded-xl px-3 py-2.5 font-bold text-sm active:scale-95 transition-transform disabled:opacity-60"
                    data-testid="in-call-press-1">
                    <span className="h-7 w-7 shrink-0 rounded-full bg-red-600 text-white flex items-center justify-center font-mono-dial">{busy === "reload_last" ? <Loader2 className="h-4 w-4 animate-spin" /> : "1"}</span>
                    Reload last plan
                  </button>
                  <button onClick={() => setShowPlans(true)} disabled={!!busy}
                    className="flex items-center gap-2 bg-white text-red-700 rounded-xl px-3 py-2.5 font-bold text-sm active:scale-95 transition-transform disabled:opacity-60"
                    data-testid="in-call-press-2">
                    <span className="h-7 w-7 shrink-0 rounded-full bg-red-600 text-white flex items-center justify-center font-mono-dial">2</span>
                    Choose a plan
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {[["standard", "$10"], ["plus", "$25"], ["pro", "$50"]].map(([id, label], i) => (
                    <button key={id} onClick={() => reload(id)} disabled={!!busy}
                      className="flex flex-col items-center bg-white text-red-700 rounded-xl px-2 py-2.5 font-bold text-sm active:scale-95 transition-transform disabled:opacity-60"
                      data-testid={`in-call-plan-${i + 1}`}>
                      <span className="h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center font-mono-dial text-xs mb-1">{busy === id ? <Loader2 className="h-3 w-3 animate-spin" /> : i + 1}</span>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="h-2" />
        </div>

        {/* Slide-up keypad */}
        {showKeypad && (
          <div className="shrink-0 border-t border-white/10 bg-black/10">
            <Dtmf isBrowser={isBrowser} onSendDigit={onSendDigit} onClose={() => setShowKeypad(false)} />
          </div>
        )}

        {/* Fixed bottom action bar */}
        <div className="shrink-0 px-6 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom))] bg-black/10 backdrop-blur-sm border-t border-white/10">
          <div className="flex items-center justify-center gap-3 mb-4">
            {isBrowser && onToggleMute && (
              <button onClick={onToggleMute} data-testid="mute-button"
                className={`flex flex-col items-center gap-1 ${muted ? "text-amber-200" : "text-white"}`}>
                <span className={`h-13 w-13 h-[52px] w-[52px] rounded-full flex items-center justify-center transition-transform active:scale-90 ${muted ? "bg-amber-400 text-amber-950" : "bg-white/15"}`}>
                  {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </span>
                <span className="text-[11px] font-semibold">{muted ? "Unmute" : "Mute"}</span>
              </button>
            )}
            <button onClick={() => setShowKeypad((v) => !v)} data-testid="keypad-toggle-button"
              className="flex flex-col items-center gap-1 text-white">
              <span className={`h-[52px] w-[52px] rounded-full flex items-center justify-center transition-transform active:scale-90 ${showKeypad ? "bg-white text-red-700" : "bg-white/15"}`}>
                <Grid3x3 className="h-5 w-5" />
              </span>
              <span className="text-[11px] font-semibold">Keypad</span>
            </button>
            <button
              onClick={() => {
                if (onToggleLoud) onToggleLoud();
                else toast.info("Use the loudspeaker button on your phone's own call screen — the audio plays through your phone.", { duration: 6000 });
              }}
              data-testid="loudspeaker-button"
              className="flex flex-col items-center gap-1 text-white"
            >
              <span className={`h-[52px] w-[52px] rounded-full flex items-center justify-center transition-transform active:scale-90 ${loud ? "bg-white text-red-700" : "bg-white/15"}`}>
                <Volume2 className="h-5 w-5" />
              </span>
              <span className="text-[11px] font-semibold">{loud ? "Boosted" : "Speaker"}</span>
            </button>
            <button onClick={openTopUp} data-testid="in-call-topup-button"
              className="flex flex-col items-center gap-1 text-white">
              <span className="h-[52px] w-[52px] rounded-full bg-white/15 flex items-center justify-center transition-transform active:scale-90">
                <Plus className="h-5 w-5" />
              </span>
              <span className="text-[11px] font-semibold">Add funds</span>
            </button>
          </div>

          {isTwilio && (
            <div className="flex items-center justify-center gap-2 text-xs text-red-200 mb-3" data-testid="speaker-hint">
              <Volume2 className="h-4 w-4" /> Hands-free? Tap the speaker button on your phone's call screen.
            </div>
          )}

          <button onClick={onEnd} disabled={ending} data-testid="end-call-button"
            className="w-full h-14 shrink-0 rounded-full bg-red-950/70 hover:bg-red-950 text-white font-bold flex items-center justify-center gap-2 transition-transform active:scale-95 disabled:opacity-60 shadow-lg border-2 border-white/30">
            {ending ? <Loader2 className="h-7 w-7 animate-spin" /> : (<><PhoneOff className="h-6 w-6" /> End call</>)}
          </button>
          <p className="text-[11px] text-red-200/80 mt-2.5 text-center">
            {isLive ? "Live call via ZEEPTEL — billed on the connected talk time." : "Simulated call — billing is live and accurate."}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};
